import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { STORE_NAMES } from '../../constants/store.constants';
import {
  ShowSystemErrorModal,
  UpdateFavoriteClinician,
  UpdateSelectedClinician,
  UpdateFilterResetOnToggle,
  SetActiveFilters
} from '../../store/actions/ClinicianListAction';
import info from '../../assets/images/info.svg';
import Image from '../Image/Image';
import { getFavoriteList } from '../../service/favorite.service';
import DialogBox from '../DialogBox/DialogBox';
import translations from '../../translations';

const Toggle = ({ setFavoriteData, setLoaderScroll, fetchClinicianList }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [savedActiveFilters, setSavedActiveFilters] = useState(null);
  const { pageConfig, activeFilters } = useSelector(state => state?.[STORE_NAMES.CLINICIAN_DIRECTORY_CLINICIANLIST]);
  const { isFavoriteToggle } = useSelector(state => state?.[STORE_NAMES.CLINICIAN_DIRECTORY_CLINICIANLIST]);
  const dispatch = useDispatch();

  const getFavoriteParams = () => ({
    region: pageConfig?.region,
    limit: 200,
    offset: 0,
    orderBy: 'lastName',
    order: 'ASC'
  });

  const getSavedRegionFilters = () => {
    if (!activeFilters?.chiclets) {
      return null;
    }

    const regionChiclets = activeFilters.chiclets.filter(chiclet => chiclet.type === 'region' && chiclet.value !== pageConfig?.region);
    if (regionChiclets.length === 0) {
      return null;
    }

    return {
      ...activeFilters,
      chiclets: regionChiclets
    };
  };

  const parseFavoriteResponse = response => {
    if (Array.isArray(response)) {
      return response;
    }
    return response?.data || [];
  };

  const handleFavoriteToggleOn = async () => {
    setSavedActiveFilters(getSavedRegionFilters());
    // Clear active filters (red icon and chiclets) when favorite toggle is ON
    dispatch(SetActiveFilters(null));

    setLoaderScroll(true);
    const response = await getFavoriteList(getFavoriteParams());
    setLoaderScroll(false);

    if (response?.error) {
      dispatch(ShowSystemErrorModal(true));
      return;
    }

    const data = parseFavoriteResponse(response);
    setFavoriteData(data);

    if (Array.isArray(data) && data.length > 0) {
      dispatch(UpdateFavoriteClinician(true));
      setIsModalOpen(false);
      return;
    }

    setIsModalOpen(true);
  };

  const handleFavoriteToggleOff = () => {
    setFavoriteData([]);
    // Restore previous chiclets and red icon when toggle is turned OFF
    dispatch(SetActiveFilters(savedActiveFilters || null));
    dispatch(UpdateFavoriteClinician(false));
    fetchClinicianList();
  };

  useEffect(() => {
    return () => {
      dispatch(UpdateFavoriteClinician(false));
    };
  }, [dispatch]);

  const onClose = () => {
    setIsModalOpen(false);
  };

  const handleToggleFavorite = async e => {
    const isChecked = e.target.checked;

    dispatch(UpdateFilterResetOnToggle(isChecked));

    if (isChecked) {
      await handleFavoriteToggleOn();
    } else {
      handleFavoriteToggleOff();
    }

    dispatch(UpdateSelectedClinician(null));
  };
  return (
    <div className={`vve-toggle-container${isFavoriteToggle ? ' vve-toggle-active' : ''}`}>
      {isFavoriteToggle && (
        <div className="vve-partition">
          <Image className="vve-img-Vertical" src={info} role="img" alt="info.svg" />
          {translations.FAVORITE_SEARCH_FILTER_DISABLE_TEXT}
        </div>
      )}
      <div className="vve-fav-div" data-testid="vve-toggle-component">
        <span className="vve-fav-label">{translations.FAVORITE}</span>

        <label className="vve-switch" aria-label={translations.FAVORITE}>
          <input type="checkbox" checked={isFavoriteToggle} onChange={handleToggleFavorite} />
          <span className="vve-slider vve-round"></span>
        </label>
      </div>
      {isModalOpen && (
        <DialogBox
          classes={{
            modalRoot: 'vve-nofav-modal'
          }}
          showModal={isModalOpen}
          modalTitle={translations.NO_FAVORITE_TITLE}
          modalBody={translations.No_FAVORITE_BODY}
          onClose={onClose}
        />
      )}
    </div>
  );
};

Toggle.propTypes = {
  setFavoriteData: PropTypes.func.isRequired,
  setLoaderScroll: PropTypes.func.isRequired,
  fetchClinicianList: PropTypes.func.isRequired
};

export default Toggle;
